import { useEffect, useRef, useState, FormEvent } from "react";
import { Send, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fmtDate } from "@/lib/api";

export function ChatPanel({ booking }: { booking: any }) {
  const [msgs, setMsgs] = useState<any[]>([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const bottom = useRef<HTMLDivElement>(null);
  const load = () => api.get(`/bookings/${booking.booking_id}/messages`).then((r) => setMsgs(r.data)).catch(() => {});

  useEffect(() => { load(); const id = setInterval(load, 10000); return () => clearInterval(id); }, [booking.booking_id]); // eslint-disable-line
  useEffect(() => { bottom.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async (e: FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try { await api.post(`/bookings/${booking.booking_id}/messages`, { body: text.trim() }); setText(""); await load(); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="mt-4 rounded-2xl border border-brand-line bg-[#efeae2] p-3" data-testid="chat-panel">
      <p className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-brand-slate"><MessageCircle className="h-3.5 w-3.5 text-brand-emerald" />Chat with {booking.caregiver_name?.split(" ")[0]} · relayed via WhatsApp, logged for safety</p>
      <div className="max-h-64 space-y-2 overflow-y-auto px-1 py-2" data-testid="chat-messages">
        {msgs.length === 0 && <p className="py-6 text-center text-xs text-brand-slate" data-testid="chat-empty">Say hello — your caregiver replies from WhatsApp.</p>}
        {msgs.map((m) => (
          <div key={m.chat_id} className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm shadow-sm ${m.sender === "family" ? "ml-auto rounded-tr-sm bg-[#d9fdd3] text-brand-navy" : "rounded-tl-sm bg-white text-brand-navy"}`} data-testid={`chat-msg-${m.sender}`}>
            <p className="text-[10px] font-semibold text-brand-slate">{m.sender === "family" ? "You" : m.sender_name}</p>{m.body}
            <p className="mt-0.5 text-right text-[10px] text-brand-slate">{fmtDate(m.created_at)}</p>
          </div>
        ))}
        <div ref={bottom} />
      </div>
      <form onSubmit={send} className="mt-2 flex gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Message your caregiver…" className="h-10 flex-1 rounded-full border border-brand-line bg-white px-4 text-sm outline-none" data-testid="chat-input" />
        <button type="submit" disabled={busy || !text.trim()} className="rounded-full bg-brand-emerald p-2.5 text-white disabled:opacity-50" aria-label="Send" data-testid="chat-send-button"><Send className="h-4 w-4" /></button>
      </form>
    </div>
  );
}
